import React from "react";

import { cn } from "../../../lib/utils";
import { useTranslation } from "../../../contexts/I18nProvider";
import { Button } from "../../../components/ui/button";
import { ChevronLeft, ChevronRight, Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentPage,
  selectShouldPayToViewMore,
  selectTotalPages,
} from "../../../redux/selectors/filterSelectors";
import { decrementPage, incrementPage } from "../../../redux/slices/filterSlice";
import { selectHasPaid } from "../../../redux/selectors/hasPaidSelectors";
import { selectSentencesLoading } from "../../../redux/selectors/sentenceSelectors";
import { triggerErrorShake } from "../../../lib/error-shake";
import { ItemsPerPageToggleGroup } from "./per-page-input";
import { setItemsPerPage, setCurrentPage as setCurrentPageAction } from "../../../redux/slices/filterSlice";

function PaginationBar() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentPage = useSelector(selectCurrentPage);
  const itemsPerPage = useSelector((state) => state.filters.itemsPerPage);
  const handlePrevPage = () => dispatch(decrementPage());
  const handleNextPage = () => dispatch(incrementPage());
  const handleItemsPerPageChange = (value) => {
    const numericValue = parseInt(value, 10);
    if (!isNaN(numericValue) && numericValue > 0) {
      dispatch(setItemsPerPage(numericValue));
      dispatch(setCurrentPageAction(1));
    }
  };
  const totalPages = useSelector(selectTotalPages);
  const shouldPayToViewMore = useSelector(selectShouldPayToViewMore);
  const sentencesLoading = useSelector(selectSentencesLoading);
  const hasPaid = useSelector(selectHasPaid);

  return (
    <div>
      <div className={cn("grid grid-cols-[auto,1fr,auto] items-center gap-4 w-full rounded-2xl bg-white/95 p-4 shadow-sm")}>
        <Button
          variant="outline"
          disabled={currentPage === 1 || sentencesLoading}
          onClick={handlePrevPage}
          className="font-semibold polyglot-charis hover:bg-primary/10 hover:border-primary rounded-lg border border-border/40 px-4 py-2 transition-all"
        >
          <ChevronLeft className="w-4 h-4" />
          {t('buttons.back')}
        </Button>
        <div className="hidden md:flex items-center justify-center gap-2">
          <div className="w-full max-w-[240px] flex items-center gap-2 group relative">
            <ItemsPerPageToggleGroup
              defaultValue={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="rounded-lg shadow-sm"
            />
            <Info className="w-4 h-4 text-muted-foreground hover:text-primary transition-colors cursor-help flex-shrink-0" 
                  title={t('polyglot.sentencesPerPageTooltip')} />
            <span className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-slate-900 text-white text-xs py-1 px-2 rounded whitespace-nowrap z-50">
              {t('polyglot.sentencesPerPageTooltip')}
            </span>
          </div>
          {sentencesLoading && (
            <span className="relative flex size-3 m-1">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/60 opacity-75"></span>
              <span className="relative inline-flex size-3 rounded-full bg-primary/30"></span>
            </span>
          )}
        </div>
        <div className="justify-self-end">
          {shouldPayToViewMore ? (
            hasPaid ? (
              <Button
                variant="outline"
                disabled={
                  currentPage === totalPages ||
                  totalPages === 0 ||
                  sentencesLoading
                }
                onClick={handleNextPage}
                className="font-semibold polyglot-charis hover:bg-primary/10 hover:border-primary rounded-lg border border-border/40 px-4 py-2 transition-all"
              >
                {t('buttons.next')}
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Dialog defaultOpen onOpenChange={() => triggerErrorShake()}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="font-semibold polyglot-charis hover:bg-primary/10 hover:border-primary rounded-lg border border-border/40 px-4 py-2 transition-all">
                    {t('buttons.next')}
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="errorShakeTrigger">
                  <DialogHeader>
                    <DialogTitle>
                      {t('messages.maxSentences')}
                    </DialogTitle>
                    <DialogDescription>
                      To access more content, please sign up and set up your
                      payment method.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter className="flex justify-end">
                    <Button variant="default">
                      <Link to="/get-started?tab=signup">Sign Up Now</Link>
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )
          ) : (
            <Button
              variant="outline"
              disabled={
                currentPage === totalPages ||
                totalPages === 0 ||
                shouldPayToViewMore ||
                sentencesLoading
              }
              onClick={handleNextPage}
              className="font-semibold polyglot-charis hover:bg-primary/10 hover:border-primary rounded-lg border border-border/40 px-4 py-2 transition-all"
            >
              {t('buttons.next')}
              <ChevronRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      <div className="md:hidden mt-4 flex items-center justify-center gap-2 rounded-xl bg-white/95 p-4 shadow-sm">
        <div className="flex items-center gap-2 group relative">
          <ItemsPerPageToggleGroup
            defaultValue={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className="rounded-lg shadow-sm"
          />
          <Info className="w-4 h-4 text-muted-foreground hover:text-primary transition-colors cursor-help flex-shrink-0" 
                title={t('polyglot.sentencesPerPageTooltip')} />
          <span className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-slate-900 text-white text-xs py-1 px-2 rounded whitespace-nowrap z-50">
            {t('polyglot.sentencesPerPageTooltip')}
          </span>
        </div>
        {sentencesLoading && (
          <span className="relative flex size-3 m-1">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/60 opacity-75"></span>
            <span className="relative inline-flex size-3 rounded-full bg-primary/30"></span>
          </span>
        )}
      </div>
    </div>
  );
}

export default PaginationBar;
